import type { ReactElement } from 'react';
import type {
  ConfigurationWithCounters,
  PotentialIssue,
  OrgConfigurationCounts,
} from '@server/homepage';
import { colors } from '@sweep-io/sweep-design';
import { ArrowUp } from '@sweep-io/sweep-design/dist/icons/ArrowUp';
import { ArrowDown } from '@sweep-io/sweep-design/dist/icons/ArrowDown';
import { Minus } from '@sweep-io/sweep-design/dist/icons/Minus';
import BlackFlag from './icons/black-flag.svg?react';
import BlueTool from './icons/blue-tool.svg?react';
import RedWarning from './icons/red-warning.svg?react';
import SadRedFace from './icons/sad-red-face.svg?react';
import {
  WORKFLOW_RULE,
  PROCESS_BUILDER_FLOW,
  FLOW,
  LAYOUT,
  APEX_CLASS,
  APEX_TRIGGER,
  APPROVAL_PROCESS,
  VALIDATION_RULE,
} from '@server/parser';
import type { SalesforceConfigurationType } from '@server/ai';

export const HIGH_SEVERITY = 'High';
export const MEDIUM_SEVERITY = 'Medium';
export const LOW_SEVERITY = 'Low';

export const ConfigurationTypeToPotentialIssueIcon: {
  [key: string]: ReactElement;
} = {
  [VALIDATION_RULE]: <SadRedFace width="100%" />,
  [APPROVAL_PROCESS]: <BlackFlag width="100%" />,
  [WORKFLOW_RULE]: <RedWarning width="100%" />,
  [PROCESS_BUILDER_FLOW]: <BlueTool width="100%" />,
  [FLOW]: <BlackFlag width="100%" />,
  [LAYOUT]: <SadRedFace width="100%" />,
  [APEX_CLASS]: <RedWarning width="100%" />,
  [APEX_TRIGGER]: <BlueTool width="100%" />,
};

export const PotentialIssueSeverityIcon: {
  [key: string]: ReactElement;
} = {
  High: <ArrowUp color={colors.blush[600]} size="medium" />,
  Medium: <Minus color={colors.blue[500]} size="medium" />,
  Low: <ArrowDown color={colors.seafoam[500]} size="medium" />,
};

export const sortBySeverity = (arr?: PotentialIssue[]) => {
  if (!arr) {
    return [];
  }
  const severityOrder = {
    [HIGH_SEVERITY]: 1,
    [MEDIUM_SEVERITY]: 2,
    [LOW_SEVERITY]: 3,
  };

  return arr.toSorted((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
};

export const sortByType = (obj: OrgConfigurationCounts): OrgConfigurationCounts => {
  const order: (keyof typeof ConfigurationWithCounters)[] = [
    'customFields',
    'recordTypes',
    'flows',
    'validationRules',
    'apexClasses',
    'apexTriggers',
    'approvalProcesses',
  ];
  return Object.fromEntries(
    order.filter((key) => key in obj).map((key) => [key, obj[key]]),
  ) as OrgConfigurationCounts;
};

export const getPotentialIssues = (items?: PotentialIssue[]) => {
  if (!items) {
    return [];
  }

  const typeOrder = [
    APEX_CLASS,
    VALIDATION_RULE,
    FLOW,
    APEX_TRIGGER,
    APPROVAL_PROCESS,
    PROCESS_BUILDER_FLOW,
    WORKFLOW_RULE,
    LAYOUT,
  ];
  const requiredCount = 4;
  const perTypeCount = 1;

  const grouped: Record<SalesforceConfigurationType, PotentialIssue[]> = {
    [APEX_CLASS]: [],
    [VALIDATION_RULE]: [],
    [FLOW]: [],
    [APEX_TRIGGER]: [],
    [APPROVAL_PROCESS]: [],
    [PROCESS_BUILDER_FLOW]: [],
    [WORKFLOW_RULE]: [],
    [LAYOUT]: [],
  };

  items.forEach((item) => {
    grouped[item.configurationType].push(item);
  });

  let result: PotentialIssue[] = [];

  for (const type of typeOrder) {
    const needed = perTypeCount - result.filter((i) => i.configurationType === type).length;
    if (needed > 0) {
      result = result.concat(grouped[type].splice(0, needed));
    }
  }

  let index = 0;
  while (result.length < requiredCount && index < typeOrder.length) {
    const type = typeOrder[index];
    while (result.length < requiredCount && grouped[type].length > 0) {
      result.push(grouped[type].shift()!);
    }
    index++;
  }

  return result;
};
